/**
 * Skin Studio (Fun section): a real 64×64 Minecraft skin editor, not an image generator.
 * Pixels of the chosen face are a grid of buttons (works with touch, mouse and keyboard; the
 * grid blocks scrolling while drawing and is separate from the 3D preview, so painting never
 * rotates the model). Designs are private to the kid; the server re-encodes every save.
 */
import { LitElement, css, html, nothing } from 'lit';
import {
  FACES,
  REGION_LABEL,
  REGIONS,
  SKIN_LIMITS,
  SKIN_PROMPTS,
  SKIN_SIZE,
  SKIN_TEMPLATES,
  allRects,
  baseHoles,
  convertModel,
  faceRect,
  floodFill,
  getPixel,
  mirrorOf,
  normalize,
  recolorRegion,
  regionAt,
  regionPixels,
  setPixel,
  type Face,
  type Layer,
  type Model,
  type Part,
  type Pixels,
  type Region,
} from '@ka/fun';
import {
  markWalkthroughSeen,
  shared,
  walkthroughSeen,
  type KaSkinViewer,
  type KaWalkthrough,
} from '@ka/ui';
import { invitation, setFun, type FunView } from './fun.ts';
import { currentKid, kidApi } from './kid.ts';
import { STEP_REGIONS, skinSteps } from './skin-walkthrough.ts';

interface SkinSummary {
  id: string;
  name: string;
  model: Model;
  png: string;
  updatedAt: string;
  revision: number;
  versionCount: number;
}
interface Design {
  id: string | null;
  name: string;
  model: Model;
  pixels: Pixels;
  revision: number;
}
type Tool = 'pencil' | 'eraser' | 'picker' | 'fill';

/** The parts a region's "Draw on it" can switch between. */
const REGION_PARTS: Record<Region, Part[]> = {
  hair: ['head'],
  face: ['head'],
  eyes: ['head'],
  skin: ['head'],
  shirt: ['body'],
  sleeves: ['rightArm', 'leftArm'],
  pants: ['rightLeg', 'leftLeg'],
  shoes: ['rightLeg', 'leftLeg'],
};
/** The selected region is shown mixed toward --ka-focus (texture data, so not CSS). */
const HIGHLIGHT = [255, 216, 102];

const PART_LABEL: Record<Part, string> = {
  head: 'Head',
  body: 'Body',
  rightArm: 'Right arm',
  leftArm: 'Left arm',
  rightLeg: 'Right leg',
  leftLeg: 'Left leg',
};
const FACE_LABEL: Record<Face, string> = {
  front: 'Front',
  back: 'Back',
  right: 'Right side',
  left: 'Left side',
  top: 'Top',
  bottom: 'Bottom',
};
const PALETTE = [
  '#000000',
  '#3b3b3b',
  '#7a7a7a',
  '#d9d9d9',
  '#ffffff',
  '#5a3a1e',
  '#8b5a2b',
  '#c69c6d',
  '#f2d2a9',
  '#a52a2a',
  '#e2594f',
  '#f2a33a',
  '#f7e26b',
  '#3f7a3a',
  '#7bc96f',
  '#2b3a7a',
  '#4a90d9',
  '#9bd3f2',
  '#6b3fa0',
  '#d97bc9',
];
const DRAFT_KEY = 'ka.fun.skin.draft';

const hex = (rgba: readonly number[]) =>
  '#' +
  rgba
    .slice(0, 3)
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('');
const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));

/** 64×64 pixels → PNG data URL, via a canvas (the browser's own encoder). */
function toPng(p: Pixels): string {
  const c = document.createElement('canvas');
  c.width = c.height = SKIN_SIZE;
  c.getContext('2d')!.putImageData(
    new ImageData(new Uint8ClampedArray(p), SKIN_SIZE, SKIN_SIZE),
    0,
    0,
  );
  return c.toDataURL('image/png');
}
const b64 = (dataUrl: string) => dataUrl.slice(dataUrl.indexOf(',') + 1);

function dataUrlBlob(url: string): Blob {
  const bin = atob(b64(url));
  return new Blob([Uint8Array.from(bin, (ch) => ch.charCodeAt(0))], { type: 'image/png' });
}

async function fromPng(src: Blob | string): Promise<Pixels> {
  // Decode data URLs here: the site's CSP (connect-src 'self') blocks fetch('data:…').
  const blob = typeof src === 'string' ? dataUrlBlob(src) : src;
  const bmp = await createImageBitmap(blob);
  if (bmp.width !== SKIN_SIZE || bmp.height !== SKIN_SIZE)
    throw new Error(
      `This picture is ${bmp.width}×${bmp.height}. Skin Studio needs a 64×64 Minecraft skin` +
        (bmp.height === 32 ? ' (older 64×32 skins are not supported yet).' : '.'),
    );
  const c = document.createElement('canvas');
  c.width = c.height = SKIN_SIZE;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0);
  return ctx.getImageData(0, 0, SKIN_SIZE, SKIN_SIZE).data;
}

/** A small flat front view (head, body, arms, legs) for design cards. */
function thumb(p: Pixels, model: Model): string {
  const src = document.createElement('canvas');
  src.width = src.height = SKIN_SIZE;
  src
    .getContext('2d')!
    .putImageData(new ImageData(new Uint8ClampedArray(p), SKIN_SIZE, SKIN_SIZE), 0, 0);
  const aw = model === 'slim' ? 3 : 4;
  const c = document.createElement('canvas');
  c.width = 8 + aw * 2;
  c.height = 32;
  const ctx = c.getContext('2d')!;
  const put = (part: Part, layer: Layer, dx: number, dy: number) => {
    const r = faceRect(part, 'front', layer, model);
    ctx.drawImage(src, r.x, r.y, r.w, r.h, dx, dy, r.w, r.h);
  };
  for (const layer of ['base', 'outer'] as Layer[]) {
    put('head', layer, aw, 0);
    put('body', layer, aw, 8);
    put('rightArm', layer, 0, 8); // the character's right arm is on the viewer's left
    put('leftArm', layer, aw + 8, 8);
    put('rightLeg', layer, aw, 20);
    put('leftLeg', layer, aw + 4, 20);
  }
  return c.toDataURL('image/png');
}

export class KaSkinStudio extends LitElement {
  static override properties = {
    designs: { state: true },
    design: { state: true },
    bodyPart: { state: true },
    face: { state: true },
    layer: { state: true },
    tool: { state: true },
    color: { state: true },
    recent: { state: true },
    mirror: { state: true },
    region: { state: true },
    view: { state: true },
    thumbs: { state: true },
    walk: { state: true },
    walkStep: { state: true },
    asCharacter: { state: true },
    preview: { state: true },
    save: { state: true },
    msg: { state: true },
    prompt: { state: true },
    versions: { state: true },
    exportOpen: { state: true },
  };
  declare designs: SkinSummary[] | null;
  declare design: Design | null;
  declare bodyPart: Part;
  declare face: Face;
  declare layer: Layer;
  declare tool: Tool;
  declare color: string;
  declare recent: string[];
  declare mirror: boolean;
  declare region: Region | null;
  declare view: 'main' | 'detail';
  /** Front-view pictures of saved designs, by id (decoded once per list load). */
  declare thumbs: Record<string, string>;
  /** The part and side last tapped on the 3D figure ("Draw on it" opens there). */
  private lastHit: { part: Part; face: Face } | null = null;
  /** The selected region glows until it is painted, so the kid then sees the real colour. */
  private tinted = false;
  /** Called after a whole region is recoloured (the walkthrough listens). */
  onRegionPainted?: (r: Region) => void;
  /** Walkthrough mode: the first-run lesson (or How to play) is running on this design. */
  declare walk: boolean;
  declare walkStep: string | null;
  /** The last step's "Make this my character" box (ticked on the first run only). */
  declare asCharacter: boolean;
  private walkChecked = false;
  /**
   * The device draft slot holds the kid's own unsaved design, so the walkthrough's design must not
   * be written there until it is saved to the account (its own work wins over a template).
   */
  private protectDraft = false;
  private steps = skinSteps({
    figure: () => this.renderRoot.querySelector(this.region ? '.tray' : '.figure'),
    nameField: () => this.finishFields(),
  });
  declare preview: string;
  declare save: 'saved' | 'unsaved' | 'saving' | 'offline' | 'conflict';
  declare msg: string;
  declare prompt: number;
  declare versions: string[] | null;
  declare exportOpen: boolean;
  private undoStack: Pixels[] = [];
  private redoStack: Pixels[] = [];
  private painting = false;
  private last: { x: number; y: number } | null = null;
  private saveTimer?: ReturnType<typeof setTimeout>;
  private previewTimer?: ReturnType<typeof setTimeout>;

  static override styles = [
    shared,
    css`
      :host {
        display: grid;
        gap: 1rem;
      }
      .row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      .btn[aria-pressed='true'] {
        background: var(--ka-gold);
        color: var(--ka-ink-on-gold);
      }
      .cards {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 0.75rem;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 8rem), 1fr));
      }
      .card {
        display: grid;
        gap: 0.4rem;
        justify-items: center;
        text-align: center;
        padding: 0.75rem;
        background: var(--ka-stone-800);
        border: 2px solid var(--ka-edge);
      }
      .card img {
        height: 96px;
        image-rendering: pixelated;
      }
      .bar {
        position: relative; /* phones: the More menu spans the bar */
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      .bar .name {
        flex: 1 1 8rem;
        min-width: 0;
        font: 600 1.1rem var(--ka-font-display);
      }
      .more {
        position: relative;
      }
      .more summary {
        list-style: none;
        box-sizing: border-box;
      }
      .more summary::-webkit-details-marker {
        display: none;
      }
      .more-body {
        position: absolute;
        right: 0;
        top: calc(100% + 0.5rem);
        z-index: 5;
        box-sizing: border-box;
        width: min(22rem, calc(100vw - 2rem));
        display: grid;
        gap: 0.75rem;
        box-shadow: var(--ka-lift);
      }
      /* Phones: the More button sits mid-row, so open the menu across the whole bar. */
      @media (max-width: 599px) {
        .more {
          position: static;
        }
        .more-body {
          left: 0;
          right: 0;
          width: auto;
        }
      }
      .stage {
        display: grid;
        gap: 1rem;
      }
      @media (min-width: 900px) {
        .stage {
          grid-template-columns: auto minmax(0, 1fr);
          align-items: start;
        }
      }
      .figure {
        display: grid;
        gap: 0.5rem;
        justify-items: center;
      }
      .figure ka-skin-viewer {
        width: min(100%, 20rem, 40vh);
      }
      .scroll-row {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding: 4px;
      }
      .scroll-row > * {
        flex: none;
      }
      .tray-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      /* Phones: the colour tray slides up from the bottom, over the page. */
      @media (max-width: 899px) {
        .stage.has-tray {
          padding-bottom: 15rem;
        }
        .tray .swatches,
        .detail .swatches {
          flex-wrap: nowrap;
          overflow-x: auto;
          padding: 4px;
        }
        .tray .swatch,
        .detail .swatch {
          flex: none;
        }
        .tray {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 4;
          max-height: 55vh;
          overflow-y: auto;
          padding: 0.75rem 1rem 1rem;
          background: var(--ka-stone-800);
          border-top: 3px solid var(--ka-edge);
          box-shadow: var(--ka-bevel);
        }
      }
      .still {
        width: 50%;
        image-rendering: pixelated;
      }
      .side,
      .tray,
      .detail {
        display: grid;
        gap: 0.75rem;
        align-content: start;
      }
      h2 {
        margin: 0;
      }
      select,
      input[type='text'] {
        font: inherit;
        min-height: 44px;
        padding: 0 0.5rem;
        background: var(--ka-stone-900);
        color: var(--ka-parchment);
        border: 2px solid var(--ka-stone-400);
      }
      label {
        display: grid;
        gap: 0.25rem;
        font-weight: 600;
      }
      .pixels {
        display: grid;
        gap: 1px;
        width: fit-content;
        background: var(--ka-edge);
        border: 3px solid var(--ka-edge);
        touch-action: none;
        user-select: none;
      }
      .px {
        all: unset;
        box-sizing: border-box;
        width: var(--cell);
        height: var(--cell);
        background-image:
          linear-gradient(45deg, #444 25%, transparent 25%, transparent 75%, #444 75%),
          linear-gradient(45deg, #444 25%, transparent 25%, transparent 75%, #444 75%);
        background-size: 8px 8px;
        background-position:
          0 0,
          4px 4px;
        background-color: #666;
        cursor: crosshair;
      }
      .px span {
        display: block;
        width: 100%;
        height: 100%;
      }
      .px:focus-visible {
        outline: 3px solid var(--ka-focus);
        z-index: 1;
      }
      .swatch {
        all: unset;
        width: 44px;
        height: 44px;
        border: 2px solid var(--ka-edge);
        cursor: pointer;
      }
      .swatch[aria-pressed='true'] {
        outline: 3px solid var(--ka-gold);
      }
      input[type='color'] {
        width: 44px;
        height: 36px;
        border: 0;
        padding: 0;
        background: none;
      }
      .map {
        width: 128px;
        image-rendering: pixelated;
        border: 2px solid var(--ka-edge);
        background: var(--ka-stone-400);
      }
      .status {
        font-size: 0.9rem;
      }
      .small {
        font-size: 0.9rem;
      }
      .warn {
        border-left: 6px solid var(--ka-gold);
      }
    `,
  ];

  constructor() {
    super();
    this.designs = null;
    this.design = null;
    this.bodyPart = 'head';
    this.face = 'front';
    this.layer = 'base';
    this.tool = 'pencil';
    this.color = '#e2594f';
    this.recent = [];
    this.mirror = false;
    this.region = null;
    this.view = 'main';
    this.thumbs = {};
    this.walk = false;
    this.walkStep = null;
    this.asCharacter = false;
    this.onRegionPainted = (r) => this.walkPainted(r);
    this.preview = '';
    this.save = 'saved';
    this.msg = '';
    this.prompt = Math.floor(Math.random() * SKIN_PROMPTS.length);
    this.versions = null;
    this.exportOpen = false;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (currentKid()) void this.loadDesigns();
    window.addEventListener('pagehide', this.flush);
  }
  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pagehide', this.flush);
    this.flush();
  }
  /** Leaving the page: keep the local draft, try the cloud save once more. */
  private flush = () => {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = undefined;
      void this.cloudSave();
    }
  };

  private async loadDesigns() {
    try {
      this.designs = (await kidApi<{ skins: SkinSummary[] }>('GET', '/fun/skins')).skins;
      for (const s of this.designs)
        void fromPng(`data:image/png;base64,${s.png}`)
          .then((p) => (this.thumbs = { ...this.thumbs, [s.id]: thumb(p, s.model) }))
          .catch(() => undefined); // the card falls back to the raw file
    } catch {
      this.msg = 'Could not load your designs. Check the connection and reload.';
      this.designs = [];
    }
  }

  private draft(): (Omit<Design, 'pixels'> & { png: string }) | null {
    try {
      return JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null');
    } catch {
      return null;
    }
  }
  private keepDraft() {
    // The slot holds the kid's own unsaved design: the walkthrough's design never takes it.
    if (this.protectDraft) return;
    const d = this.design;
    if (!d) return;
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ ...d, pixels: undefined, png: toPng(d.pixels) }),
      );
    } catch {
      /* storage full or private mode: cloud save still works */
    }
  }

  // ---------- opening designs ----------

  private open(d: Design, saved = true) {
    this.protectDraft = false;
    this.design = d;
    this.undoStack = [];
    this.redoStack = [];
    this.versions = null;
    this.exportOpen = false;
    this.region = null;
    this.view = 'main';
    this.lastHit = null;
    this.save = saved && d.id ? 'saved' : 'unsaved';
    this.msg = '';
    this.refreshPreview(0);
    if (!saved) this.keepDraft();
    // The editor replaces the gallery: start at its top, not where the tapped card was.
    void this.updateComplete.then(() => scrollTo({ top: 0 }));
  }

  private async openSaved(s: SkinSummary) {
    try {
      this.open({
        id: s.id,
        name: s.name,
        model: s.model,
        pixels: await fromPng(`data:image/png;base64,${s.png}`),
        revision: s.revision,
      });
    } catch (e) {
      this.msg = e instanceof Error ? e.message : 'Could not open that design.';
    }
  }

  private async recover() {
    const d = this.draft();
    if (!d) return;
    this.open({ ...d, pixels: await fromPng(d.png) }, false);
  }

  private async importFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > 64 * 1024) {
      this.msg = 'That file is too big for a skin (64 KB at most).';
      return;
    }
    try {
      const pixels = await fromPng(file);
      this.open(
        {
          id: null,
          name:
            file.name.replace(/\.png$/i, '').slice(0, SKIN_LIMITS.nameLength) || 'Imported skin',
          model: 'classic',
          pixels,
          revision: 0,
        },
        false,
      );
      this.msg = 'Imported. If the arms look cut off, switch the model to Slim below.';
    } catch (err) {
      this.msg = err instanceof Error ? err.message : 'That file did not work.';
    }
  }

  // ---------- painting ----------

  private get rect() {
    return faceRect(this.bodyPart, this.face, this.layer, this.design!.model);
  }

  private snapshot() {
    this.undoStack = [...this.undoStack.slice(-49), this.design!.pixels.slice()];
    this.redoStack = [];
  }

  private paintAt(x: number, y: number) {
    const d = this.design!;
    const r = this.rect;
    const paint = (part: Part, face: Face, px: number, py: number) => {
      const fr = faceRect(part, face, this.layer, d.model);
      if (this.tool === 'pencil')
        setPixel(d.pixels, fr.x + px, fr.y + py, [...rgb(this.color), 255]);
      else if (this.tool === 'eraser') setPixel(d.pixels, fr.x + px, fr.y + py, [0, 0, 0, 0]);
      else if (this.tool === 'fill') floodFill(d.pixels, fr, px, py, [...rgb(this.color), 255]);
    };
    if (this.tool === 'picker') {
      const c = getPixel(d.pixels, r.x + x, r.y + y);
      if (c[3]) this.pick(hex(c));
      this.tool = 'pencil';
      return;
    }
    if (this.tool === 'eraser' && this.layer === 'base') return;
    paint(this.bodyPart, this.face, x, y);
    if (this.mirror) {
      const m = mirrorOf(this.bodyPart, this.face, x, y, d.model);
      paint(m.part, m.face, m.x, m.y);
    }
    this.requestUpdate();
  }

  private cellFromEvent(e: PointerEvent) {
    const el = (this.renderRoot as ShadowRoot).elementFromPoint(
      e.clientX,
      e.clientY,
    ) as HTMLElement | null;
    const btn = el?.closest<HTMLElement>('.px');
    return btn ? { x: Number(btn.dataset.x), y: Number(btn.dataset.y) } : null;
  }

  private down(e: PointerEvent) {
    const c = this.cellFromEvent(e);
    if (!c || !this.design) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.snapshot();
    this.painting = this.tool === 'pencil' || this.tool === 'eraser';
    this.last = c;
    this.paintAt(c.x, c.y);
    if (!this.painting) this.changed();
  }
  private move(e: PointerEvent) {
    if (!this.painting) return;
    const c = this.cellFromEvent(e);
    if (!c) return;
    // Fill every cell between the last one and this one, so fast strokes have no gaps.
    const from = this.last ?? c;
    const steps = Math.max(Math.abs(c.x - from.x), Math.abs(c.y - from.y));
    for (let i = 1; i <= steps; i++)
      this.paintAt(
        Math.round(from.x + ((c.x - from.x) * i) / steps),
        Math.round(from.y + ((c.y - from.y) * i) / steps),
      );
    if (!steps) this.paintAt(c.x, c.y);
    this.last = c;
  }
  private up() {
    if (!this.painting) return;
    this.painting = false;
    this.last = null;
    this.changed(); // one undo step and one save per stroke, never per pixel
  }
  /** Keyboard: Enter/Space on a pixel paints it as one stroke. */
  private key(e: KeyboardEvent, x: number, y: number) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    this.snapshot();
    this.paintAt(x, y);
    this.changed();
  }

  private changed() {
    this.save = 'unsaved';
    this.keepDraft();
    this.refreshPreview();
    clearTimeout(this.saveTimer);
    if (this.design?.id) this.saveTimer = setTimeout(() => void this.cloudSave(), 3000);
  }

  private undo() {
    const prev = this.undoStack.pop();
    if (!prev || !this.design) return;
    this.redoStack.push(this.design.pixels);
    this.design = { ...this.design, pixels: prev };
    this.changed();
  }
  private redo() {
    const next = this.redoStack.pop();
    if (!next || !this.design) return;
    this.undoStack.push(this.design.pixels);
    this.design = { ...this.design, pixels: next };
    this.changed();
  }

  private pick(c: string) {
    this.color = c;
    this.recent = [c, ...this.recent.filter((r) => r !== c)].slice(0, 8);
  }

  private refreshPreview(delay = 150) {
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => {
      const d = this.design;
      if (!d) return;
      let p = d.pixels;
      if (this.region && this.tinted && this.view === 'main') {
        p = p.slice();
        for (const [x, y] of regionPixels(this.region, d.model)) {
          const c = getPixel(p, x, y);
          setPixel(p, x, y, [
            ...c.slice(0, 3).map((v, i) => Math.round(v * 0.55 + HIGHLIGHT[i]! * 0.45)),
            c[3],
          ]);
        }
      }
      this.preview = toPng(p);
    }, delay);
  }

  private switchModel(to: Model) {
    const d = this.design!;
    if (to === d.model) return;
    const msg =
      to === 'slim'
        ? 'Slim arms are 3 pixels wide, so one column of each arm is removed. Your current design stays as it is; this makes a copy.'
        : 'Classic arms are 4 pixels wide, so one column of each arm is doubled. Your current design stays as it is; this makes a copy.';
    if (!confirm(msg)) return;
    this.open(
      {
        id: null,
        name: `${d.name} (${to})`.slice(0, SKIN_LIMITS.nameLength),
        model: to,
        pixels: convertModel(d.pixels, d.model, to),
        revision: 0,
      },
      false,
    );
  }

  // ---------- saving ----------

  private async cloudSave(): Promise<void> {
    const d = this.design;
    if (!d) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = undefined;
    this.save = 'saving';
    const body = { name: d.name.trim() || 'My skin', model: d.model, png: b64(toPng(d.pixels)) };
    try {
      if (d.id) {
        const r = await kidApi<{ skin: SkinSummary }>('PUT', `/fun/skins/${d.id}`, {
          ...body,
          expectedRevision: d.revision,
        });
        if (this.design === d) this.design = { ...d, revision: r.skin.revision };
      } else {
        const r = await kidApi<{ skin: SkinSummary; fun: FunView; newRewards: string[] }>(
          'POST',
          '/fun/skins',
          body,
        );
        setFun(r.fun);
        if (this.design === d) this.design = { ...d, id: r.skin.id, revision: r.skin.revision };
        if (r.newRewards.length)
          this.msg = 'New for your character! Look in My character to equip it.';
      }
      this.save = 'saved';
      void this.loadDesigns();
    } catch (e) {
      const m = e instanceof Error ? e.message : '';
      if (/another tab or device/.test(m)) this.save = 'conflict';
      else {
        this.save = 'offline';
        this.msg = m || 'Could not save to your account. Your work is kept on this device.';
      }
    }
  }

  private async saveAsCopy() {
    const d = this.design!;
    this.design = {
      ...d,
      id: null,
      name: `${d.name} (copy)`.slice(0, SKIN_LIMITS.nameLength),
      revision: 0,
    };
    await this.cloudSave();
  }

  private async useAsCharacter() {
    const d = this.design!;
    if (baseHoles(d.pixels, d.model)) {
      this.exportOpen = true;
      return;
    }
    if (this.save !== 'saved') await this.cloudSave();
    if (!this.design?.id) return;
    try {
      const r = await kidApi<{ fun: FunView }>('PUT', '/fun/character-skin', {
        skinId: this.design.id,
      });
      setFun(r.fun);
      this.msg = 'This is now your character on the website (only you see it).';
    } catch (e) {
      this.msg = e instanceof Error ? e.message : 'Could not set your character.';
    }
  }

  private async showVersions() {
    if (!this.design?.id) return;
    try {
      const s = await kidApi<{ png: string; versions: string[] }>(
        'GET',
        `/fun/skins/${this.design.id}`,
      );
      this.versions = s.versions;
    } catch {
      this.msg = 'Could not load older saves.';
    }
  }

  private async restoreVersion(png: string) {
    this.snapshot();
    this.design = { ...this.design!, pixels: await fromPng(`data:image/png;base64,${png}`) };
    this.versions = null;
    this.changed();
  }

  private download() {
    const d = this.design!;
    const a = document.createElement('a');
    a.href = toPng(normalize(d.pixels, d.model));
    a.download = `${(d.name.trim() || 'skin').replace(/[^\w -]+/g, '')}.png`;
    a.click();
  }

  private fillGaps() {
    const d = this.design!;
    this.snapshot();
    this.design = { ...d, pixels: normalize(d.pixels, d.model, rgb(this.color)) };
    this.changed();
  }

  private async deleteDesign(s: SkinSummary) {
    if (!confirm(`Delete “${s.name}”? This cannot be undone.`)) return;
    try {
      const r = await kidApi<{ fun: FunView }>('DELETE', `/fun/skins/${s.id}`);
      setFun(r.fun);
      await this.loadDesigns();
    } catch (e) {
      this.msg = e instanceof Error ? e.message : 'Could not delete.';
    }
  }

  // ---------- views ----------

  private home() {
    const draft = this.draft();
    const n = this.designs?.length ?? 0;
    return html`<section class="panel">
        <p class="small"><a href="/fun">← Fun</a></p>
        <h1>Skin Studio</h1>
        <p>
          Paint your own Minecraft skin, turn it around in 3D, and download it to use in Minecraft.
        </p>
        <p class="small muted">
          Your designs are private. Up to ${SKIN_LIMITS.designs} designs (${n} used).
        </p>
        ${this.msg ? html`<p role="alert">${this.msg}</p>` : nothing}
        <p><button class="btn" @click=${() => this.startWalk(false)}>How to play</button></p>
        ${
          draft
            ? html`<p class="row">
                <button class="btn green" @click=${this.recover}>Continue “${draft.name}”</button>
                <span class="small muted">unsaved work on this device</span>
              </p>`
            : nothing
        }
      </section>
      <section class="panel">
        <h2>Make a new one</h2>
        <ul class="cards">
          ${SKIN_TEMPLATES.map((t) => {
            const p = t.make('classic');
            return html`<li class="card">
              <img src=${thumb(p, 'classic')} alt="" />
              <strong>${t.name}</strong>
              <button
                class="btn green"
                @click=${() => this.open({ id: null, name: `My ${t.name.toLowerCase()}`, model: 'classic', pixels: p, revision: 0 }, false)}
              >
                Start
              </button>
            </li>`;
          })}
          <li class="card">
            <label class="btn"
              >Import a PNG <input type="file" accept="image/png" hidden @change=${this.importFile}
            /></label>
            <span class="small muted">A 64×64 skin file</span>
          </li>
        </ul>
      </section>
      <section class="panel">
        <h2>My designs</h2>
        ${
          this.designs === null
            ? html`<p class="muted">Loading…</p>`
            : this.designs.length
              ? html`<ul class="cards">
                  ${this.designs.map(
                    (s) =>
                      html`<li class="card">
                        <img src=${this.thumbs[s.id] ?? `data:image/png;base64,${s.png}`} alt="" />
                        <strong>${s.name}</strong>
                        <span class="row">
                          <button class="btn" @click=${() => this.openSaved(s)}>Edit</button>
                          <button
                            class="btn"
                            aria-label="Delete ${s.name}"
                            @click=${() => this.deleteDesign(s)}
                          >
                            Delete
                          </button>
                        </span>
                      </li>`,
                  )}
                </ul>`
              : html`<p class="muted">No designs yet — start from a template above.</p>`
        }
      </section>`;
  }

  private editor(d: Design) {
    const saveText = {
      saved: d.id ? 'Saved' : '',
      // Kept nowhere when the draft slot is the kid's other design (after a walkthrough).
      unsaved: d.id ? 'Saving soon…' : this.protectDraft ? 'Not kept — tap Save' : 'Not saved yet',
      saving: 'Saving…',
      offline: 'Saved on this device only',
      conflict: 'Changed somewhere else',
    }[this.save];
    const w = this.walk;
    return html`<section class="panel">
        <div class="bar">
          ${w ? nothing : html`<button class="btn" @click=${this.leave}>← My skins</button>`}
          <input
            class="name"
            type="text"
            aria-label="Name"
            maxlength=${SKIN_LIMITS.nameLength}
            .value=${d.name}
            @change=${(e: Event) => ((this.design = { ...d, name: (e.target as HTMLInputElement).value }), this.changed())}
          />
          ${
            w
              ? nothing
              : html`<span class="status small muted" role="status">${saveText}</span>
                  ${this.save !== 'saved' || !d.id ? html`<button class="btn primary" @click=${this.cloudSave}>Save</button>` : nothing}
                  ${this.more(d)}`
          }
        </div>
        ${
          this.save === 'conflict'
            ? html`<p class="row warn panel">
                This design was changed on another tab or device.
                <button class="btn" @click=${this.saveAsCopy}>Keep mine as a copy</button>
                <button
                  class="btn"
                  @click=${async () => {
                    const s =
                      this.designs?.find((x) => x.id === d.id) ??
                      (await this.loadDesigns(), this.designs?.find((x) => x.id === d.id));
                    if (s) void this.openSaved(s);
                  }}
                >
                  Load the other version
                </button>
              </p>`
            : nothing
        }
        ${this.msg ? html`<p role="alert">${this.msg}</p>` : nothing}
      </section>
      ${this.view === 'detail' && this.region ? this.detail(d, this.region) : this.main(d)}`;
  }

  private leave() {
    const d = this.design!;
    if (
      this.protectDraft &&
      !d.id &&
      this.undoStack.length &&
      !confirm('This design is not saved and will be lost. Leave it?')
    )
      return;
    this.flush();
    this.design = null;
    void this.loadDesigns();
  }

  /** Everything a kid rarely needs, out of the way. */
  private more(d: Design) {
    const holes = baseHoles(d.pixels, d.model);
    return html`<details class="more">
      <summary class="btn">More</summary>
      <div class="panel more-body">
        <label
          >Model
          <select
            @change=${(e: Event) => this.switchModel((e.target as HTMLSelectElement).value as Model)}
          >
            <option value="classic" ?selected=${d.model === 'classic'}>
              Classic (4-pixel arms)
            </option>
            <option value="slim" ?selected=${d.model === 'slim'}>Slim (3-pixel arms)</option>
          </select></label
        >
        <p class="small">
          Idea: ${SKIN_PROMPTS[this.prompt]}
          <button
            class="link"
            @click=${() => (this.prompt = (this.prompt + 1) % SKIN_PROMPTS.length)}
          >
            Another idea
          </button>
        </p>
        <p class="row">
          <button class="btn" @click=${() => (this.exportOpen = !this.exportOpen)}>Download</button>
          <button class="btn" @click=${() => this.startWalk(false)}>How to play</button>
          ${
            d.id
              ? html`<button class="btn" @click=${this.showVersions}>Older saves</button>
                  <button class="btn" @click=${this.saveAsCopy}>Save as copy</button>`
              : nothing
          }
        </p>
        ${this.versions ? html`<div class="row">${this.versions.length ? this.versions.map((v, i) => html`<button class="card" @click=${() => this.restoreVersion(v)}><img src="data:image/png;base64,${v}" alt="" style="height:64px" /><span class="small">Save ${i + 1} back</span></button>`) : html`<p class="small muted">No older saves yet.</p>`}</div>` : nothing}
        ${
          this.exportOpen
            ? html`<div class="panel ${holes ? 'warn' : ''}">
                ${
                  holes
                    ? html`<p>
                          ${holes} pixel${holes === 1 ? ' is' : 's are'} see-through on the base
                          layer. Minecraft would show ${holes === 1 ? 'it' : 'them'} as a hole or as
                          black.
                        </p>
                        <p>
                          <button class="btn" @click=${this.fillGaps}>
                            Fill gaps with my colour
                          </button>
                        </p>`
                    : nothing
                }
                <p class="row">
                  <button class="btn green" @click=${this.download}>Download PNG</button>
                  <img class="map" src=${this.preview} alt="The whole skin file" />
                </p>
                <p class="small">
                  To use it in Minecraft Java Edition: open the Minecraft Launcher →
                  <strong>Skins</strong> → <strong>New skin</strong> → browse to the file, choose
                  <strong>${d.model === 'slim' ? 'Slim' : 'Classic'}</strong>, then save. Other
                  editions work differently.
                </p>
              </div>`
            : nothing
        }
      </div>
    </details>`;
  }

  /** The colours: recent first, then the palette, then any colour. */
  private swatches(choose: (c: string) => void, selected = true) {
    return html`<div class="row swatches" role="group" aria-label="Colours">
      ${[...this.recent, ...PALETTE.filter((p) => !this.recent.includes(p))].map(
        (p) =>
          html`<button
            class="swatch"
            style="background:${p}"
            aria-label="Colour ${p}"
            aria-pressed=${selected && this.color === p}
            @click=${() => choose(p)}
          ></button>`,
      )}
      <label class="small"
        >Any colour
        <input
          type="color"
          .value=${this.color}
          @change=${(e: Event) => choose((e.target as HTMLInputElement).value)}
      /></label>
    </div>`;
  }

  private get viewer() {
    return this.renderRoot.querySelector<KaSkinViewer>('ka-skin-viewer');
  }

  /** The main view: the character, big. Tap a part (or its button) to colour it. */
  private main(d: Design) {
    const r = this.region;
    return html`<section class="panel stage ${r ? 'has-tray' : ''}">
      <div class="figure">
        <ka-skin-viewer
          pickable
          .skin=${this.preview}
          .model=${d.model}
          label="Your character"
          @ka-skin-pick=${this.onPick}
          @ka-skin-turned=${() => this.walkStep === 'spin' && this.coach?.complete('spin')}
        >
          <img class="still" src=${thumb(d.pixels, d.model)} alt="Your character, front" />
        </ka-skin-viewer>
        <button class="btn" @click=${() => this.viewer?.turnAround()}>Turn around</button>
      </div>
      <div class="side">
        <div class="row" role="group" aria-label="Parts">
          ${(this.walk ? REGIONS.filter((x) => x === this.walkRegions[0]) : REGIONS).map(
            (x) =>
              html`<button class="btn" aria-pressed=${x === r} @click=${() => this.selectRegion(x)}>
                ${REGION_LABEL[x]}
              </button>`,
          )}
        </div>
        ${
          r
            ? html`<div class="tray" role="group" aria-label="${REGION_LABEL[r]}">
                <div class="tray-head">
                  <h2>${REGION_LABEL[r]}</h2>
                  <button class="link" @click=${() => this.selectRegion(null)}>Close</button>
                </div>
                ${this.swatches((c) => this.paintRegion(r, c), false)}
                <p class="row">
                  ${this.walk ? nothing : html`<button class="btn green" @click=${this.openDetail}>Draw on it</button>`}
                  <button class="btn" ?disabled=${!this.undoStack.length} @click=${this.undo}>
                    Undo
                  </button>
                  <button class="btn" ?disabled=${!this.redoStack.length} @click=${this.redo}>
                    Redo
                  </button>
                </p>
              </div>`
            : html`<p class="muted">Tap a part of your character to colour it.</p>`
        }
        ${d.id && !this.walk ? html`<p><button class="btn" @click=${this.useAsCharacter}>Use as my character</button></p>` : nothing}
      </div>
    </section>`;
  }

  private onPick(e: CustomEvent<{ x: number; y: number }>) {
    const m = this.design!.model;
    const { x, y } = e.detail;
    const r = regionAt(m, x, y);
    const hit = allRects(m).find(
      ({ rect }) => x >= rect.x && x < rect.x + rect.w && y >= rect.y && y < rect.y + rect.h,
    );
    if (r) this.selectRegion(r, hit && { part: hit.part, face: hit.face });
  }

  private selectRegion(r: Region | null, hit?: { part: Part; face: Face }) {
    if (this.walk && r && !this.walkRegions.includes(r)) return; // only the step's part
    this.region = r;
    this.tinted = !!r;
    this.lastHit = hit ?? null;
    this.refreshPreview(0);
  }

  /** Recolours a whole region as one undo step. */
  private paintRegion(r: Region, color: string) {
    const d = this.design!;
    this.pick(color);
    this.tinted = false;
    this.snapshot();
    this.design = { ...d, pixels: recolorRegion(d.pixels, r, d.model, [...rgb(color), 255]) };
    this.changed();
    this.onRegionPainted?.(r);
  }

  /** "Draw on it": pixel painting on the tapped part and side. */
  private openDetail() {
    const parts = REGION_PARTS[this.region!];
    const hit = this.lastHit;
    this.bodyPart = hit && parts.includes(hit.part) ? hit.part : parts[0]!;
    this.face = hit?.face ?? 'front';
    this.layer = 'base';
    if (this.tool === 'eraser') this.tool = 'pencil';
    this.view = 'detail';
    this.tinted = false;
    void this.updateComplete.then(() =>
      this.renderRoot.querySelector('.detail')?.scrollIntoView({ block: 'start' }),
    );
  }

  private setLayer(l: Layer) {
    this.layer = l;
    if (l === 'base' && this.tool === 'eraser') this.tool = 'pencil';
  }

  private detail(d: Design, region: Region) {
    const r = this.rect;
    const size = Math.min(innerWidth * 0.92 - 48, 448);
    // Fit the width, and keep the swatches under the grid on screen (spec: never below the fold).
    const fit = Math.min(size / Math.max(r.w, r.h * 0.75), (innerHeight * 0.42) / r.h);
    const cell = `${Math.max(16, Math.min(48, Math.floor(fit)))}px`;
    const parts = REGION_PARTS[region];
    const tool = (t: Tool, label: string) =>
      html`<button class="btn" aria-pressed=${this.tool === t} @click=${() => (this.tool = t)}>
        ${label}
      </button>`;
    return html`<section class="panel detail">
      <div class="row">
        <button class="btn green" @click=${() => ((this.view = 'main'), this.refreshPreview(0))}>
          Done
        </button>
        <h2>${REGION_LABEL[region]}</h2>
      </div>
      ${
        parts.length > 1
          ? html`<div class="row" role="group" aria-label="Part">
              ${parts.map((p) => html`<button class="btn" aria-pressed=${p === this.bodyPart} @click=${() => (this.bodyPart = p)}>${PART_LABEL[p]}</button>`)}
            </div>`
          : nothing
      }
      <div class="row scroll-row" role="group" aria-label="Side">
        ${FACES.map((f) => html`<button class="btn" aria-pressed=${f === this.face} @click=${() => (this.face = f)}>${FACE_LABEL[f]}</button>`)}
      </div>
      <div
        class="pixels"
        role="grid"
        aria-label="Pixels of ${PART_LABEL[this.bodyPart]} ${FACE_LABEL[this.face]}"
        style="grid-template-columns: repeat(${r.w}, var(--cell)); --cell: ${cell}"
        @pointerdown=${this.down}
        @pointermove=${this.move}
        @pointerup=${this.up}
        @pointercancel=${this.up}
        @lostpointercapture=${this.up}
      >
        ${Array.from({ length: r.h }, (_, y) =>
          Array.from({ length: r.w }, (_, x) => {
            const c = getPixel(d.pixels, r.x + x, r.y + y);
            const base = faceRect(this.bodyPart, this.face, 'base', d.model);
            const under =
              this.layer === 'outer' ? getPixel(d.pixels, base.x + x, base.y + y) : null;
            const bg = c[3] ? hex(c) : under ? `${hex(under)}55` : 'transparent';
            return html`<button
              class="px"
              role="gridcell"
              data-x=${x}
              data-y=${y}
              aria-label="Row ${y + 1}, column ${x + 1}: ${c[3] ? hex(c) : 'see-through'}"
              @keydown=${(e: KeyboardEvent) => this.key(e, x, y)}
            >
              <span style="background:${bg}"></span>
            </button>`;
          }),
        )}
      </div>
      ${this.swatches((c) => this.pick(c))}
      <div class="row" role="group" aria-label="Tools">
        ${tool('pencil', 'Pen')} ${tool('fill', 'Bucket')} ${tool('picker', 'Eyedropper')}
        ${this.layer === 'outer' ? tool('eraser', 'Eraser') : nothing}
        <button
          class="btn"
          aria-pressed=${this.mirror}
          @click=${() => (this.mirror = !this.mirror)}
        >
          Mirror
        </button>
        <button class="btn" ?disabled=${!this.undoStack.length} @click=${this.undo}>Undo</button>
        <button class="btn" ?disabled=${!this.redoStack.length} @click=${this.redo}>Redo</button>
      </div>
      <p>
        <button
          class="btn"
          aria-pressed=${this.layer === 'outer'}
          @click=${() => this.setLayer(this.layer === 'outer' ? 'base' : 'outer')}
        >
          Hat & jacket layer
        </button>
        <span class="small muted">
          ${this.layer === 'outer' ? 'Sits on top and can be see-through.' : ''}
        </span>
      </p>
    </section>`;
  }

  // ---------- walkthrough ----------

  private get coach() {
    return this.renderRoot.querySelector<KaWalkthrough>('ka-walkthrough');
  }

  /** The parts the current step accepts (none on talking steps). */
  private get walkRegions(): Region[] {
    return (this.walk && this.walkStep && STEP_REGIONS[this.walkStep]) || [];
  }

  /** Starts the walkthrough on a new Explorer design, never losing the kid's open work. */
  private async startWalk(auto: boolean) {
    const d = this.design;
    if (d?.id && this.save !== 'saved') await this.cloudSave();
    // A never-saved design is already in the device draft slot; the walkthrough's design is
    // not written there until the walkthrough ends (keepDraft).
    const hadDraft = !!this.draft();
    this.walk = true;
    this.asCharacter = auto;
    const t = SKIN_TEMPLATES[0]!;
    // open(…, true) does not touch the draft slot; then keep this design there only if the slot
    // was free, so a failed "Save it" still leaves it on the device.
    this.open(
      {
        id: null,
        name: `My ${t.name.toLowerCase()}`,
        model: 'classic',
        pixels: t.make('classic'),
        revision: 0,
      },
      true,
    );
    this.protectDraft = hadDraft;
    this.keepDraft();
  }

  /** A colouring step is done when its part was painted (face also colours skin, shirt sleeves). */
  private walkPainted(r: Region) {
    const all = this.walkRegions;
    if (!this.walkStep || !all.includes(r)) return;
    let p = this.design!.pixels;
    for (const x of all)
      if (x !== r) p = recolorRegion(p, x, this.design!.model, [...rgb(this.color), 255]);
    if (p !== this.design!.pixels) {
      this.design = { ...this.design!, pixels: p };
      this.changed();
    }
    this.coach?.complete(this.walkStep);
  }

  private onWalkStep(e: CustomEvent<{ id: string }>) {
    this.walkStep = e.detail.id;
    this.view = 'main';
    this.selectRegion(null);
    this.msg = '';
    // Keyboard users land on the one part button the step asks for (after the card's heading).
    if (this.walkRegions.length)
      void this.updateComplete.then(() =>
        this.renderRoot.querySelector<HTMLElement>('[aria-label=Parts] button')?.focus(),
      );
  }

  /** "Save it": save, maybe set as the character, then finish (or show why not, and stay). */
  private async onWalkAction() {
    if (this.save === 'saving') return; // a double tap must not save two designs
    await this.cloudSave();
    if (!this.design?.id || this.save !== 'saved') return;
    if (this.asCharacter) await this.useAsCharacter();
    this.coach?.complete('finish');
  }

  private onWalkEnd() {
    markWalkthroughSeen('skin-studio');
    this.walk = false;
    this.walkStep = null;
    if (this.design && this.save !== 'saved') this.keepDraft();
  }

  private finishFields() {
    const d = this.design;
    if (!d) return nothing;
    return html`<label
        >Name
        <input
          type="text"
          maxlength=${SKIN_LIMITS.nameLength}
          .value=${d.name}
          @input=${(e: Event) => (this.design = { ...this.design!, name: (e.target as HTMLInputElement).value })}
      /></label>
      <label class="check"
        ><input
          type="checkbox"
          .checked=${this.asCharacter}
          @change=${(e: Event) => (this.asCharacter = (e.target as HTMLInputElement).checked)}
        />
        Make this my character</label
      >
      ${this.msg ? html`<p class="err small" role="alert">${this.msg}</p>` : nothing}`;
  }

  override updated() {
    if (this.walk) this.coach?.requestUpdate(); // its last step shows this element's state
    if (!this.walkChecked && this.designs !== null && !this.design) {
      this.walkChecked = true;
      if (!walkthroughSeen('skin-studio') && !this.draft()) void this.startWalk(true);
    }
  }

  override render() {
    if (!currentKid()) return invitation();
    return html`${this.design ? this.editor(this.design) : this.home()}
      <ka-walkthrough
        placement="top"
        .steps=${this.steps}
        ?open=${this.walk}
        @ka-walkthrough-step=${this.onWalkStep}
        @ka-walkthrough-action=${this.onWalkAction}
        @ka-walkthrough-end=${this.onWalkEnd}
      ></ka-walkthrough>`;
  }
}
customElements.define('ka-skin-studio', KaSkinStudio);
